// v231 admin API client for the Pull-Gate-Policy-Engine (item 47 of
// PRODUCT-ROADMAP.md, "Pull-Gate-Policy-Engine — Image-Firewall für
// Pulls"; item 53 in the recommended delivery order, Phase B).
//
// The engine and its formatutil hook landed in Phase A; this client
// backs the AdminPullGate page that ships in Phase B — master toggle,
// policy editor, audit-log viewer, and live-preview "if enabled now,
// X% would be blocked" against the most-recent artifacts.

import useSWR from 'swr';

export type SyncMode =
  | 'pre-scan-cache'
  | 'block-until-scan'
  | 'optimistic-with-quarantine';

export type PullGateAction = 'allow' | 'block' | 'bypass';

export type PolicyScope = 'global' | 'project' | 'repo';

export interface PullGateSettings {
  enabled: boolean;
  sync_mode: SyncMode;
  grace_period_hours: number;
  fallback_action: 'allow' | 'block';
  updated_at: string;
  updated_by?: string;
}

export interface PullGatePolicy {
  id: string;
  scope_kind: PolicyScope;
  scope_id?: string;
  name: string;
  enabled: boolean;
  require_signed: boolean;
  max_severity?: string;        // LOW|MEDIUM|HIGH|CRITICAL or ""
  max_age_days?: number;        // 0 = no cap
  require_sbom: boolean;
  allowed_licenses: string[];
  forbidden_licenses: string[];
  bypass_patterns: string[];
  block_message_template?: string;
  created_at: string;
  created_by?: string;
  updated_at: string;
}

export interface PullGatePolicyInput {
  scope_kind: PolicyScope;
  scope_id?: string;
  name: string;
  enabled?: boolean;
  require_signed: boolean;
  max_severity?: string;
  max_age_days?: number;
  require_sbom: boolean;
  allowed_licenses: string[];
  forbidden_licenses: string[];
  bypass_patterns: string[];
  block_message_template?: string;
}

export interface PullGateAuditRow {
  id: number;
  artifact_id?: string;
  artifact_path: string;
  artifact_sha256?: string;
  repo_id?: string;
  principal_kind: string;
  principal_email?: string;
  decision: PullGateAction;
  reason: string;
  matched_policy?: string;
  policy_name?: string;
  severity_seen?: string;
  occurred_at: string;
}

export interface PullGatePreview {
  sampled: number;
  allow: number;
  block: number;
  bypass: number;
  block_pct: number;
  by_reason: Record<string, number>;
  generated_at: string;
  engine_enabled: boolean;
}

export interface PullGateStats {
  today: number;
  last_7d: number;
  last_30d: number;
  all_time: number;
  /** 24 hourly buckets of block counts; index 0 = oldest, 23 = newest. */
  sparkline: number[];
  blocks_by_policy: { name: string; count: number }[];
  generated_at: string;
  engine_enabled: boolean;
}

export interface PullGateLiveEvent {
  decision: PullGateAction;
  reason: string;
  artifact_path?: string;
  artifact_sha256?: string;
  repo_id?: string;
  principal_kind?: string;
  principal_email?: string;
  matched_policy?: string;
  policy_name?: string;
  severity_seen?: string;
  occurred_at: string;
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function usePullGateSettings() {
  return useSWR<PullGateSettings>('/api/admin/pull-gate/settings', json, {
    refreshInterval: 15_000,
  });
}

export function usePullGatePolicies() {
  return useSWR<{ policies: PullGatePolicy[] }>(
    '/api/admin/pull-gate/policies',
    json,
    { refreshInterval: 15_000 },
  );
}

export function usePullGateAudit(decision?: PullGateAction, limit = 100) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (decision) params.set('decision', decision);
  return useSWR<{ rows: PullGateAuditRow[] }>(
    `/api/admin/pull-gate/audit?${params.toString()}`,
    json,
    { refreshInterval: 15_000 },
  );
}

export function usePullGatePreview(sample = 200) {
  return useSWR<PullGatePreview>(
    `/api/admin/pull-gate/preview?sample=${sample}`,
    json,
    {
      // Preview is expensive (200 evaluations per call) — refresh on
      // explicit user click via mutate(), not on a fast timer.
      refreshInterval: 0,
      revalidateOnFocus: false,
    },
  );
}

export function usePullGateStats() {
  return useSWR<PullGateStats>('/api/admin/pull-gate/stats', json, {
    refreshInterval: 30_000,
  });
}

/** SSE URL for the live decision stream. The page opens this with
 *  EventSource directly so credentials cookies travel along (the
 *  fetch() Authorization-header form would not). */
export const PULL_GATE_STREAM_URL = '/api/admin/pull-gate/stream';

export async function updatePullGateSettings(
  input: Pick<PullGateSettings, 'enabled' | 'sync_mode' | 'grace_period_hours' | 'fallback_action'>,
): Promise<void> {
  const res = await fetch('/api/admin/pull-gate/settings', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function createPullGatePolicy(
  input: PullGatePolicyInput,
): Promise<PullGatePolicy> {
  const res = await fetch('/api/admin/pull-gate/policies', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

export async function updatePullGatePolicy(
  id: string,
  input: PullGatePolicyInput,
): Promise<void> {
  const res = await fetch(
    `/api/admin/pull-gate/policies/${encodeURIComponent(id)}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function deletePullGatePolicy(id: string): Promise<void> {
  const res = await fetch(
    `/api/admin/pull-gate/policies/${encodeURIComponent(id)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok && res.status !== 204)
    throw new Error(`${res.status}: ${await res.text()}`);
}
